import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/components/ui/use-toast";
import { Copy, Users, PoundSterling, Menu } from "lucide-react";
import { format } from "date-fns";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { GetAffiliateStatsResponse } from "@/types/billing";
import MenuBar from "@/components/MenuBar";
import { useAuth } from "@/hooks/useAuth";
import { useCountry } from "@/contexts/CountryContext";

export default function AffiliateDashboard() {
  const { toast } = useToast();
  const { user } = useAuth();
  const { formatCurrency } = useCountry();
  const [stats, setStats] = useState<GetAffiliateStatsResponse["stats"] | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchAffiliateStats();
  }, []);

  const fetchAffiliateStats = async () => {
    try {
      setIsLoading(true);
      const response = await ApiService.request<GetAffiliateStatsResponse>(
        EndpointKeys.GET_AFFILIATE_STATS,
      );
      setStats(response.stats);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load affiliate statistics",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const copyReferralCode = async () => {
    if (!stats?.affiliate.referral_code) return;
    await navigator.clipboard.writeText(
      `${window.location.origin}/billing?ref=${stats.affiliate.referral_code}`,
    );
    toast({
      title: "Copied!",
      description: "Referral link copied to clipboard",
    });
  };

  if (isLoading) {
    return (
      <div className="h-96 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile Menu Toggle */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 flex items-center p-4 bg-gray-900">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-white"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </div>

      {/* Sidebar */}
      <div
        className={`${
          isMobileMenuOpen ? "block" : "hidden"
        } md:block fixed md:relative w-64 h-full bg-gray-900 z-20`}
      >
        <MenuBar />
      </div>

      {/* Main Content */}
      <div className="flex-1 w-full md:w-auto mt-16 md:mt-0">
        <div className="p-4 md:p-6 max-w-7xl space-y-6">
          {/* Referral Link Card */}
          <Card>
            <CardHeader>
              <CardTitle>
                {user?.userType === "PARTNER"
                  ? `${user.companyName}'s Referral Link`
                  : "Your Referral Link"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-muted p-4 rounded-lg flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <ScrollArea className="w-full">
                  <code className="text-sm break-all">
                    {window.location.origin}/billing?ref=
                    {stats.affiliate.referral_code}
                  </code>
                </ScrollArea>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyReferralCode}
                  className="shrink-0"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Link
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Share this link to earn {formatCurrency(50)} for each successful referral!
              </p>
            </CardContent>
          </Card>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Total Referrals
                    </p>
                    <h2 className="text-3xl font-bold">
                      {stats.affiliate.total_referrals}
                    </h2>
                  </div>
                  <Users className="h-8 w-8 text-primary" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Total Commission
                    </p>
                    <h2 className="text-3xl font-bold">
                      {formatCurrency(Number(stats.affiliate.total_commission_earned))}
                    </h2>
                  </div>
                  <PoundSterling className="h-8 w-8 text-primary" />
                </div>
              </CardContent>
            </Card>

            <Card className="sm:col-span-2 md:col-span-1">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Pending Commission
                    </p>
                    <h2 className="text-3xl font-bold">
                      {formatCurrency(Number(stats.unpaid_commission))}
                    </h2>
                  </div>
                  <PoundSterling className="h-8 w-8 text-primary" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Transactions Table */}
          <Card>
            <CardHeader>
              <CardTitle>Referral History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Commission</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.transactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell className="whitespace-nowrap">
                          {format(new Date(transaction.created_at), "PP")}
                        </TableCell>
                        <TableCell>#{transaction.referred_user_id}</TableCell>
                        <TableCell>{formatCurrency(transaction.commission_amount)}</TableCell>
                        <TableCell>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              transaction.is_paid
                                ? "bg-green-100 text-green-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {transaction.is_paid ? "Paid" : "Pending"}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                    {stats.transactions.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          className="text-center text-muted-foreground"
                        >
                          No referrals yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
